import React, { useEffect, useState } from "react";
import { Download, Search, FileText } from "lucide-react";
import api from "@/lib/api";

export default function AdminAuditLogsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [entity, setEntity] = useState("");
  const [actor, setActor] = useState("");
  const [selected, setSelected] = useState(null);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const load = async () => {
    const params = { q: q || undefined, action: action || undefined, entity: entity || undefined, actor_id: actor || undefined, limit, offset };
    try {
      const { data } = await api.get("/admin/audit-logs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, action, entity, actor, offset]);

  const download = () => {
    const token = window.localStorage.getItem("gcw_bearer") || "";
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/audit-logs/export.csv?action=${encodeURIComponent(action)}&entity=${encodeURIComponent(entity)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "audit-logs.csv";
        document.body.appendChild(link); link.click(); link.remove();
      });
  };

  return (
    <div className="max-w-6xl" data-testid="admin-audit-logs">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Audit</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Audit logs</h1>
          <p className="text-[#252A34]/70 text-sm mt-1">Every sensitive action is recorded. {total.toLocaleString()} record{total === 1 ? "" : "s"}.</p>
        </div>
        <button onClick={download} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium" data-testid="audit-export">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4 grid gap-3 sm:grid-cols-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#252A34]/50" />
          <input value={q} onChange={(e) => { setOffset(0); setQ(e.target.value); }} placeholder="Search" className="w-full rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-sm" data-testid="audit-search" />
        </div>
        <input value={action} onChange={(e) => { setOffset(0); setAction(e.target.value); }} placeholder="Action (regex)" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="audit-action" />
        <input value={entity} onChange={(e) => { setOffset(0); setEntity(e.target.value); }} placeholder="Entity (collection)" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="audit-entity" />
        <input value={actor} onChange={(e) => { setOffset(0); setActor(e.target.value); }} placeholder="Actor ID" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="audit-actor" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#252A34]/60 bg-slate-50">
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">Actor</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Entity</th>
              <th className="px-3 py-2">Record</th>
              <th className="px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="p-10 text-center text-[#252A34]/60">No records.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(r)} data-testid={`audit-row-${r.id}`}>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                <td className="px-3 py-2 text-xs">{r.actor_role || "—"}<div className="text-[10px] text-[#252A34]/50">{r.actor_id}</div></td>
                <td className="px-3 py-2 text-xs"><code className="bg-slate-100 px-1.5 py-0.5 rounded">{r.action}</code></td>
                <td className="px-3 py-2 text-xs">{r.entity || "—"}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.record_id || "—"}</td>
                <td className="px-3 py-2 text-xs truncate max-w-[200px]">{r.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-[#252A34]/70">
        <div>Showing {items.length ? offset + 1 : 0}–{offset + items.length} of {total}</div>
        <div className="flex gap-1">
          <button disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - limit))} className="rounded border border-slate-200 bg-white px-3 py-1.5 disabled:opacity-50">Prev</button>
          <button disabled={offset + limit >= total} onClick={() => setOffset((o) => o + limit)} className="rounded border border-slate-200 bg-white px-3 py-1.5 disabled:opacity-50">Next</button>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4" onClick={() => setSelected(null)}>
          <div className="max-w-2xl w-full rounded-2xl bg-white shadow-2xl p-6 max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="audit-detail">
            <div className="font-display text-lg font-semibold text-[#071b22]">{selected.action}</div>
            <pre className="mt-3 text-xs bg-slate-50 rounded-lg border border-slate-100 p-3 overflow-auto">{JSON.stringify(selected, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
